import { useMemo, useState, useCallback, ReactNode } from 'react'

import { Currency, CurrencyAmount } from '@cowprotocol/currency'

import { AffiliateTraderRewardsRow, useIsRewardsRowEnabled } from 'modules/affiliate'
import { useBridgeQuoteAmounts } from 'modules/bridge'
import {
  getTotalCosts,
  TradeFeesAndCosts,
  TradeTotalCostsDetails,
  useDerivedTradeState,
  NetworkCostsRow,
  useShouldPayGas,
  useGetReceiveAmountInfo,
  useGetSwapReceiveAmountInfo,
} from 'modules/trade'
import { useTradeQuote } from 'modules/tradeQuote'
import { useIsSlippageModified, useShouldShowSlippageProminent, useTradeSlippage } from 'modules/tradeSlippage'
import { useUsdAmount } from 'modules/usdAmount'

import { QuoteApiError } from 'api/cowProtocol/errors/QuoteError'
import { NetworkCostsSuffix } from 'common/pure/NetworkCostsSuffix'
import { RateInfoParams } from 'common/pure/RateInfo'

import { NetworkCostsTooltipSuffix } from '../../pure/NetworkCostsTooltipSuffix'
import { RowDeadline } from '../RowDeadline'
import { RowQuoteId } from '../RowQuoteId'
import { RowSlippage } from '../RowSlippage'

interface TradeRateDetailsProps {
  deadline: number
  rateInfoParams: RateInfoParams
  isTradePriceUpdating: boolean
  quoteId?: string | number | null
  quoteVerified?: boolean | null
  quoteExpiration?: string | null
  accordionContent?: ReactNode
  feeWrapper?: (feeElement: ReactNode, isOpen: boolean) => ReactNode
}

export function TradeRateDetails({
  rateInfoParams,
  deadline,
  isTradePriceUpdating,
  quoteId,
  quoteVerified,
  quoteExpiration,
  accordionContent,
  feeWrapper,
}: TradeRateDetailsProps): ReactNode {
  const [isFeeDetailsOpen, setFeeDetailsOpen] = useState(false)

  const slippage = useTradeSlippage()
  const isSlippageModified = useIsSlippageModified()
  const shouldShowSlippageProminent = useShouldShowSlippageProminent()
  const receiveAmountInfo = useGetReceiveAmountInfo()
  const swapReceiveAmountInfo = useGetSwapReceiveAmountInfo()
  const shouldPayGas = useShouldPayGas()
  const isRewardsRowEnabled = useIsRewardsRowEnabled()
  const bridgeQuoteAmounts = useBridgeQuoteAmounts()
  const { error: quoteError } = useTradeQuote()

  const networkFeeAmount = useNetworkFeeAmount()

  const networkFeeAmountUsd = useUsdAmount(networkFeeAmount).value

  const toggleAccordion = useCallback(() => {
    setFeeDetailsOpen((prev) => !prev)
  }, [])

  if (!receiveAmountInfo || !swapReceiveAmountInfo || quoteError) {
    if (!networkFeeAmount) return null
    return (
      <div style={{ padding: '0 10px' }}>
        <NetworkCostsRow
          networkFeeAmount={networkFeeAmount}
          networkFeeAmountUsd={networkFeeAmountUsd}
          amountSuffix={shouldPayGas ? <NetworkCostsSuffix /> : null}
          tooltipSuffix={<NetworkCostsTooltipSuffix />}
        />
      </div>
    )
  }

  // Slippage row component - can be shown outside or inside accordion
  const slippageRow = slippage && (
    <RowSlippage
      isTradePriceUpdating={isTradePriceUpdating}
      allowedSlippage={slippage}
      isSlippageModified={isSlippageModified}
    />
  )
  // Default expanded content if accordionContent prop is not supplied
  const defaultExpandedContent = (
    <>
      <TradeFeesAndCosts
        receiveAmountInfo={receiveAmountInfo}
        networkCostsSuffix={shouldPayGas ? <NetworkCostsSuffix /> : null}
        networkCostsTooltipSuffix={<NetworkCostsTooltipSuffix />}
      />
      {slippageRow} {/* Always show slippage inside accordion */}
      {isRewardsRowEnabled && <AffiliateTraderRewardsRow />}
      <RowDeadline deadline={deadline} />
      <RowQuoteId quoteId={quoteId} isVerified={quoteVerified} expiration={quoteExpiration} />
    </>
  )
  return (
    <>
      <TradeTotalCostsDetails
        totalCosts={getTotalCosts(
          swapReceiveAmountInfo,
          bridgeQuoteAmounts?.bridgeFeeAmounts?.amountInIntermediateCurrency,
        )}
        rateInfoParams={rateInfoParams}
        isFeeDetailsOpen={isFeeDetailsOpen}
        toggleAccordion={toggleAccordion}
        feeWrapper={feeWrapper}
      >
        {accordionContent || defaultExpandedContent}
      </TradeTotalCostsDetails>

      {/* Show slippage outside accordion when prominent and accordion is closed (to avoid duplication) */}
      {shouldShowSlippageProminent && !isFeeDetailsOpen && (
        <div style={{ padding: '0 6px', marginTop: '-5px' }}>{slippageRow}</div>
      )}
    </>
  )
}

function useNetworkFeeAmount(): CurrencyAmount<Currency> | null {
  const derivedTradeState = useDerivedTradeState()
  const tradeQuote = useTradeQuote()

  const inputCurrency = derivedTradeState?.inputCurrency

  const errorData = tradeQuote?.error as QuoteApiError<{ fee_amount: string }> | undefined
  const costsExceedFeeRaw = errorData instanceof QuoteApiError ? errorData.data?.fee_amount : undefined

  return useMemo(() => {
    if (!costsExceedFeeRaw || !inputCurrency) return null
    return CurrencyAmount.fromRawAmount(inputCurrency, costsExceedFeeRaw)
  }, [costsExceedFeeRaw, inputCurrency])
}
